/**
 * DeliveryConfirmDialog — conferma delle quantità ricevute prima di chiudere
 * l'ordine. Le righe sono precompilate con il rimanente e si possono
 * modificare in aumento (arrivato più del previsto → la quantità dell'ordine
 * si allinea) o in diminuzione (l'ordine resta "Consegnato parzialmente" e si
 * potrà completare più tardi).
 */
import React, { useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { AlertTriangle, CheckCircle2, Clock, Loader2, Minus, PackageCheck, Plus } from 'lucide-react';
import { toast } from 'sonner';

const MAX_QTY = 999;

export default function DeliveryConfirmDialog({ order, onClose, onDelivered }) {
  const [qty, setQty] = useState({});
  const [saving, setSaving] = useState(false);

  const lines = useMemo(
    () => (order?.items || []).map(it => ({ ...it, remaining: Math.max(0, it.qty - (it.qty_delivered || 0)) })),
    [order],
  );

  useEffect(() => {
    if (!order) return;
    const initial = {};
    (order.items || []).forEach(it => {
      initial[it.product_id] = Math.max(0, it.qty - (it.qty_delivered || 0));
    });
    setQty(initial);
  }, [order]);

  const summary = useMemo(() => {
    let received = 0;
    let missing = 0;
    const increased = [];
    lines.forEach(it => {
      const v = qty[it.product_id] || 0;
      received += v;
      if (v < it.remaining) missing += it.remaining - v;
      if (v > it.remaining) increased.push(it.name);
    });
    return { received, missing, increased, closes: missing === 0 };
  }, [lines, qty]);

  if (!order) return null;

  const setQ = (pid, v) => setQty(prev => ({ ...prev, [pid]: Math.max(0, Math.min(MAX_QTY, Number.isFinite(v) ? v : 0)) }));
  const fillAll = () => setQty(Object.fromEntries(lines.map(it => [it.product_id, it.remaining])));
  const clearAll = () => setQty(Object.fromEntries(lines.map(it => [it.product_id, 0])));

  const submit = async () => {
    const items = lines
      .map(it => ({ product_id: it.product_id, qty: qty[it.product_id] || 0 }))
      .filter(it => it.qty > 0);
    if (items.length === 0) { toast.error('Indica almeno un articolo ricevuto'); return; }
    setSaving(true);
    try {
      const r = await api.post(`/orders/${order.order_id}/deliver`, { items });
      const bumped = r.data.quantities_increased || [];
      toast.success(
        r.data.status === 'delivered'
          ? `Ordine ${order.number} chiuso — consegnato`
          : `Consegna parziale registrata — ${order.number} resta da completare`,
      );
      if (bumped.length > 0) toast.info(`Quantità aggiornate: ${bumped.join(' · ')}`);
      onDelivered();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Consegna fallita');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!order} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="delivery-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PackageCheck className="w-5 h-5" /> Conferma quantità consegnate
          </DialogTitle>
          <DialogDescription>
            Controlla quante unità sono arrivate davvero: puoi aumentare o diminuire ogni riga.
            Se è arrivato tutto l'ordine viene chiuso, altrimenti resta parzialmente ricevuto e lo completi più tardi.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-7 rounded-none text-xs" onClick={fillAll}
                  data-testid="delivery-fill-all-btn">Ricevuto tutto</Button>
          <Button variant="outline" size="sm" className="h-7 rounded-none text-xs" onClick={clearAll}
                  data-testid="delivery-clear-all-btn">Azzera</Button>
        </div>

        <div className="border border-slate-200 divide-y divide-slate-100" data-testid="delivery-items">
          {lines.map(it => {
            const v = qty[it.product_id] || 0;
            return (
              <div key={it.product_id} className="flex items-center gap-2 px-3 py-2" data-testid={`delivery-item-${it.product_id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-slate-800 truncate">{it.name}</p>
                  <p className="text-[11px] text-slate-400">
                    Ordinati {it.qty}
                    {(it.qty_delivered || 0) > 0 && ` · già ricevuti ${it.qty_delivered}`}
                    {it.remaining > 0 ? ` · da ricevere ${it.remaining}` : ' · completo'}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="outline" size="icon" className="h-7 w-7 rounded-none" disabled={v === 0}
                          onClick={() => setQ(it.product_id, v - 1)}
                          data-testid={`delivery-qty-minus-${it.product_id}`}>
                    <Minus className="w-3 h-3" />
                  </Button>
                  <Input type="number" min={0} max={MAX_QTY} value={v}
                         onChange={e => setQ(it.product_id, parseInt(e.target.value, 10))}
                         className={`h-7 w-14 rounded-none text-center text-sm px-1 ${v > it.remaining ? 'border-amber-400 text-amber-700 font-semibold' : ''}`}
                         data-testid={`delivery-qty-input-${it.product_id}`} />
                  <Button variant="outline" size="icon" className="h-7 w-7 rounded-none" disabled={v >= MAX_QTY}
                          onClick={() => setQ(it.product_id, v + 1)}
                          data-testid={`delivery-qty-plus-${it.product_id}`}>
                    <Plus className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {summary.increased.length > 0 && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 p-2 flex gap-1.5"
             data-testid="delivery-increase-warning">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span>Hai indicato più del previsto per {summary.increased.join(', ')}: la quantità dell'ordine verrà aggiornata.</span>
          </p>
        )}

        <p className={`text-xs p-2 border flex gap-1.5 ${summary.closes
          ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
          : 'text-cyan-700 bg-cyan-50 border-cyan-200'}`} data-testid="delivery-outcome-hint">
          {summary.closes ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <Clock className="w-4 h-4 shrink-0" />}
          <span>
            {summary.closes
              ? `${summary.received} unità ricevute — l'ordine verrà chiuso come Consegnato.`
              : `${summary.received} unità ricevute, ${summary.missing} ancora mancanti — l'ordine resterà Consegnato parzialmente e potrai completarlo più tardi.`}
          </span>
        </p>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="delivery-cancel-btn">Annulla</Button>
          <Button onClick={submit} disabled={saving} data-testid="delivery-confirm-btn"
                  className={`gap-1.5 ${summary.closes ? 'bg-blue-600 hover:bg-blue-700' : 'bg-cyan-600 hover:bg-cyan-700'}`}>
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {summary.closes ? 'Chiudi come consegnato' : 'Registra consegna parziale'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
